import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { SeatIndicator } from "@/components/SeatIndicator";
import { AttendeeDialog } from "@/components/AttendeeDialog";
import { AudienceBadge } from "@/components/AudienceIcon";
import { useAuth } from "@/context/AuthContext";
import api, { apiErrorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Clock, ArrowLeft, Share2, Ticket, Users } from "lucide-react";
import { priceLabel } from "@/lib/price";

const FALLBACK = "https://images.unsplash.com/photo-1585701456523-3daa9cfee779?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const resolveImg = (url) => (!url ? FALLBACK : url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export default function ProgramPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attProgram, setAttProgram] = useState(null);
  const [attBusy, setAttBusy] = useState(false);
  const [booked, setBooked] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/${id}`);
      setP(data);
    } catch { setP(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const openBooking = () => {
    if (!user) { navigate("/belepes"); return; }
    setAttProgram(p);
  };
  const confirmBooking = async (refs) => {
    setAttBusy(true);
    try {
      await api.post("/bookings", { program_id: p.program_id, attendees: refs });
      toast.success("Foglalás rögzítve! Jóváhagyás után e-mailt küldök. ✿");
      setAttProgram(null);
      setBooked(true);
      load();
    } catch (e) { toast.error(apiErrorMsg(e.response?.data?.detail)); }
    finally { setAttBusy(false); }
  };
  const share = async () => {
    const url = `${window.location.origin}/api/share/${p.program_id}`;
    try {
      if (navigator.share) { await navigator.share({ title: `${p.title} · sorról sorra`, text: `${p.title} – ${p.start_date} ${p.start_time}`, url }); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Program link másolva – oszd meg bárhol! ✿");
    } catch (_) { /* megszakítva */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream"><Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center text-inkmuted" data-testid="program-loading">Betöltés…</div>
      </div>
    );
  }
  if (!p) {
    return (
      <div className="min-h-screen bg-cream"><Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="program-notfound">
          <h1 className="font-serif text-3xl text-ink">Ezt a programot nem találom</h1>
          <button onClick={() => navigate("/#timetable")} data-testid="back-to-programs-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-poppy text-white px-6 py-3 hover:bg-poppy/90 transition-colors"><ArrowLeft size={16} /> Vissza a programokhoz</button>
        </div>
      </div>
    );
  }

  const full = p.available_seats <= 0;
  const alreadyBooked = booked;
  const started = !!p.start_date && new Date(`${p.start_date}T${p.start_time || "00:00"}:00`) <= new Date();
  return (
    <div className="min-h-screen bg-cream" data-testid="program-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 pt-28 pb-20">
        <button onClick={() => navigate("/#timetable")} data-testid="back-to-programs-btn"
          className="inline-flex items-center gap-2 text-sm text-inkmuted hover:text-poppy transition-colors mb-6"><ArrowLeft size={16} /> Vissza a programokhoz</button>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div className="relative">
            <img src={resolveImg(p.photo_url)} alt={p.title} data-testid="program-image"
              className="w-full h-[340px] object-cover rounded-[2rem] shadow-xl" />
            <button onClick={share} data-testid="share-btn"
              className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-xs bg-white/90 backdrop-blur text-ink rounded-full px-3 py-2 shadow hover:bg-white transition-colors"><Share2 size={13} /> Megosztás</button>
          </div>

          <div>
            <h1 className="font-serif text-4xl text-ink leading-tight" data-testid="program-title">{p.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-inkmuted">
              <span className="inline-flex items-center gap-1.5" data-testid="program-date"><Calendar size={15} className="text-poppy" /> {p.start_date}</span>
              <span className="inline-flex items-center gap-1.5 text-base font-bold"><Clock size={16} strokeWidth={2} className="text-poppy" /> {p.start_time}–{p.end_time}</span>
              <AudienceBadge audience={p.audience} />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              {!p.pass_only && <span className="font-serif text-2xl font-medium text-poppy" data-testid="program-price">{priceLabel(p.price, p.audience)}</span>}
              {(p.pass_only || p.pass_eligible) && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5"
                  style={{ backgroundColor: "#E5F3F2", color: "#2E9E99" }}>
                  <Ticket size={13} /> {p.pass_only ? "csak bérlettel" : "bérlettel is"}
                </span>
              )}
            </div>

            {p.description && <p className="mt-5 text-ink/80 leading-relaxed whitespace-pre-line" data-testid="program-description">{p.description}</p>}

            <div className="mt-6 max-w-sm"><SeatIndicator info={p} showCount={false} /></div>

            <div className="mt-7">
              {p.cancelled ? (
                <span className="text-sm font-medium text-red-500" data-testid="program-cancelled">Ez a program elmarad.</span>
              ) : alreadyBooked ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-teal/10 text-teal font-medium px-6 py-3" data-testid="program-booked">Lefoglalva ✓</span>
              ) : started ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-stone-200 text-ink/60 font-medium px-6 py-3" data-testid="program-started">Ez az esemény már elkezdődött</span>
              ) : full ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-stone-200 text-ink/60 font-medium px-6 py-3" data-testid="program-full">Betelt</span>
              ) : (
                <button onClick={openBooking} data-testid="program-book-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-poppy text-white text-base px-8 py-3.5 hover:bg-poppy/90 transition-colors shadow-lg shadow-poppy/20"><Users size={18} /> Foglalás</button>
              )}
            </div>
          </div>
        </div>
      </div>

      <AttendeeDialog open={!!attProgram} onOpenChange={(v) => { if (!v) setAttProgram(null); }}
        program={attProgram} adultName={user?.name} childrenList={user?.children || []}
        initialRefs={["adult"]} busy={attBusy} onConfirm={confirmBooking} />
    </div>
  );
}
